// Custom drag preview for Scheduler game blocks.
// The native HTML5 drag image loses its grab offset for the absolutely
// positioned blocks inside the horizontally scrolled timeline, so we hide it
// (see GameBlock's getEmptyImage) and render our own preview here, driven by
// the same getSourceClientOffset() the drop math in Field.jsx uses. This keeps
// the preview and the landing slot perfectly in sync.
//
// The preview is the real GameBlock rendered in ghost/preview mode, so it looks
// pixel-identical to the block being dragged.

import { useMemo } from 'react';
import { useDragLayer } from 'react-dnd';
import GameBlock, { ItemTypes } from './GameBlock';
import './GameDragLayer.scss';

const noop = () => {};

export default function GameDragLayer() {
  const { isDragging, itemType, item, sourceOffset } = useDragLayer(
    (monitor) => ({
      isDragging: monitor.isDragging(),
      itemType: monitor.getItemType(),
      item: monitor.getItem(),
      sourceOffset: monitor.getSourceClientOffset(),
    }),
  );

  // `item` is stable for the whole drag, so the ghost block only rebuilds when
  // the dragged block changes — not on every pointer move (sourceOffset).
  const ghost = useMemo(() => {
    if (itemType !== ItemTypes.GAME_BLOCK || !item) return null;
    return (
      <GameBlock
        id={item.tournamentId}
        game={item.game}
        color={item.color}
        preview
        highlighted={false}
        activeSearchHoverKey={null}
        setBracketsPopup={noop}
        onSearchTermClick={noop}
        onSearchHoverChange={noop}
      />
    );
  }, [item, itemType]);

  if (!isDragging || !ghost || !sourceOffset) return null;

  return (
    <div className='game-drag-layer'>
      <div
        className='ghost-wrap'
        style={{
          transform: `translate(${sourceOffset.x}px, ${sourceOffset.y}px)`,
          width: item.width,
        }}
      >
        {ghost}
      </div>
    </div>
  );
}
